import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import getCroppedImg from "../../helpers/getCroppedImg";
import { uploadImages } from "../../functions/uploadImages";
import { useSelector } from "react-redux";
import { updateCover } from "../../functions/user";
import { createPost } from "../../functions/post";
import PulseLoader from "react-spinners/PulseLoader";
import OldCovers from "./OldCovers";
import "../../styles/profile.css";

export default function Cover({ cover, visitor, photos }) {
  const [coverPicture, setCoverPicture] = useState("");
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [showPostDialog, setShowPostDialog] = useState(false);
  const [currentCover, setCurrentCover] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [, setIsRepositioning] = useState(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [coverRemoved, setCoverRemoved] = useState(false);
  const { user } = useSelector((state) => ({ ...state }));
  // Determine account privacy from user or localStorage fallback
  const accountPrivacy = (user && (user.privacy || user?.profile?.privacy)) || localStorage.getItem("accountPrivacy") || "public";
  const refInput = useRef(null);
  const cRef = useRef(null);
  const [error, setError] = useState("");
  const handleImage = (e) => {
    let file = e.target.files[0];
    if (
      file.type !== "image/jpeg" &&
      file.type !== "image/png" &&
      file.type !== "image/webp" &&
      file.type !== "image/gif" &&
      file.type !== "image/avif"
    ) {
      setError(`${file.name} format is not supported.`);
      return;
    } else if (file.size > 1024 * 1024 * 5) {
      setError(`${file.name} is too large max 5mb allowed.`);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      setCoverPicture(event.target.result);
    };
  };
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);
  const getCroppedImage = useCallback(
    async (show) => {
      try {
        const img = await getCroppedImg(coverPicture, croppedAreaPixels);
        if (show) {
          setZoom(1);
          setCrop({ x: 0, y: 0 });
          setCoverPicture(img);
        } else {
          return img;
        }
      } catch (error) {
        console.log(error);
      }
    },
    [croppedAreaPixels, coverPicture]
  );
  const coverRef = useRef(null);
  const coverDropdownBtnRef = useRef(null);
  const coverDropdownMenuRef = useRef(null);
  const [width, setWidth] = useState();

  const MOBILE_COVER_DROPDOWN_MAX = 768;
  const COVER_DROPDOWN_GAP = 8;
  const COVER_DROPDOWN_EDGE = 12;

  const getCoverDropdownSafeInsets = () => {
    const root = document.documentElement;
    const styles = getComputedStyle(root);
    const safeTop = parseFloat(styles.getPropertyValue("env(safe-area-inset-top)")) || 0;
    const safeRight = parseFloat(styles.getPropertyValue("env(safe-area-inset-right)")) || 0;
    const navOffset =
      parseFloat(styles.getPropertyValue("--mobile-nav-safe-offset")) || 96;
    return {
      top: safeTop + 72,
      bottom: navOffset + 16,
      right: safeRight + COVER_DROPDOWN_EDGE,
    };
  };

  const positionCoverDropdownMenu = useCallback(() => {
    const menu = coverDropdownMenuRef.current;
    const btn = coverDropdownBtnRef.current;
    if (!menu || !btn || window.innerWidth > MOBILE_COVER_DROPDOWN_MAX) {
      if (menu) {
        menu.style.cssText = "";
        menu.classList.remove("cover_dropdown_menu--positioned");
      }
      return;
    }

    const { top: safeTop, bottom: safeBottom, right: safeRight } =
      getCoverDropdownSafeInsets();
    const rect = btn.getBoundingClientRect();
    const menuHeight = menu.offsetHeight || menu.scrollHeight;
    const viewportH = window.innerHeight;
    const maxTop = viewportH - safeBottom - menuHeight;

    let top = rect.top - menuHeight - COVER_DROPDOWN_GAP;
    if (top < safeTop) {
      top = rect.bottom + COVER_DROPDOWN_GAP;
    }
    top = Math.max(safeTop, Math.min(top, maxTop));

    const menuWidth = Math.min(200, window.innerWidth - safeRight * 2);
    let right = window.innerWidth - rect.right;
    right = Math.max(safeRight, right);

    menu.classList.add("cover_dropdown_menu--positioned");
    menu.style.position = "fixed";
    menu.style.top = `${top}px`;
    menu.style.right = `${right}px`;
    menu.style.left = "auto";
    menu.style.bottom = "auto";
    menu.style.width = `${menuWidth}px`;
    menu.style.maxHeight = `${viewportH - safeTop - safeBottom}px`;
    menu.style.zIndex = "10150";
  }, []);
  useEffect(() => {
    const handleResize = () => {
      if (coverRef.current) {
        setWidth(coverRef.current.clientWidth);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const updateCoverPicture = async (createPostOption = null) => {
    try {
      setLoading(true);
      let img = await getCroppedImage();
      let blob = await fetch(img).then((b) => b.blob());
      const path = `${user.username}/cover_pictures`;
      let formData = new FormData();
      formData.append("file", blob);
      formData.append("path", path);
      const res = await uploadImages(formData, path, user.token);

      // Check if uploadImages returned an error message instead of array
      if (typeof res === 'string') {
        setLoading(false);
        setError(res);
        return;
      }

      const updated_picture = await updateCover(res[0].url, user.token);
      if (updated_picture === "ok") {
        setLoading(false);
        setCoverPicture("");
        // Avoid direct DOM assignment (cRef.current may be null). Use state to drive UI.
        setCurrentCover(res[0].url);
        setCoverRemoved(false); // Reset removed state when new photo is uploaded

        // Show dialog to ask if user wants to create a post
        if (createPostOption === null) {
          setShowPostDialog(true);
          // Store the image data for later use
          window.tempCoverImageData = res;
        } else if (createPostOption === true) {
          // Create publication
          await createCoverPost(res);
        }
        // If createPostOption is false, just skip creating post

      } else {
        setLoading(false);
        setError(updated_picture);
      }
    } catch (error) {
      setLoading(false);
      setError(error.response?.data?.message || error.message || "An error occurred while updating cover photo");
    }
  };

  const createCoverPost = async (imageData) => {
    try {
      const new_post = await createPost(
        "coverPicture",
        null,
        null,
        imageData,
        user.id,
        user.token
      );
      console.log(new_post);
      if (new_post.status !== "ok") {
        setError(new_post.message);
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message || "An error occurred while creating post");
    }
  };

  const handlePostDialogResponse = async (createPost) => {
    setShowPostDialog(false);
    if (createPost && window.tempCoverImageData) {
      await createCoverPost(window.tempCoverImageData);
    }
    // Clean up temp data
    delete window.tempCoverImageData;
  };

  // Handle dropdown option clicks
  const handleUploadPhoto = () => {
    setShowDropdown(false);
    refInput.current.click();
  };

  const handleReposition = () => {
    setShowDropdown(false);
    setIsRepositioning(true);
    setCoverPicture(currentCover || cover);
  };

  const handleRemovePhoto = () => {
    setShowDropdown(false);
    setShowRemoveConfirmation(true);
  };

  const confirmRemovePhoto = async () => {
    setShowRemoveConfirmation(false);
    try {
      setLoading(true);
      const updated_picture = await updateCover("", user.token);
      if (updated_picture === "ok") {
        setCurrentCover(null);
        setCoverRemoved(true);
        setLoading(false);
      } else {
        setLoading(false);
        setError(updated_picture);
      }
    } catch (error) {
      setLoading(false);
      setError(error.response?.data?.message || error.message || "An error occurred while removing cover photo");
    }
  };

  const cancelRemovePhoto = () => {
    setShowRemoveConfirmation(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.cover_dropdown_container')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  useLayoutEffect(() => {
    if (!showDropdown) {
      const menu = coverDropdownMenuRef.current;
      if (menu) {
        menu.style.cssText = "";
        menu.classList.remove("cover_dropdown_menu--positioned");
      }
      return;
    }

    positionCoverDropdownMenu();
    const rafId = requestAnimationFrame(positionCoverDropdownMenu);

    const handleReposition = () => positionCoverDropdownMenu();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [showDropdown, positionCoverDropdownMenu]);
  return (
    <div className="profile_cover" ref={coverRef}>
      {coverPicture && (
        <div className="save_changes_cover">
          <div className="save_changes_left">
            {accountPrivacy === 'private' ? (
              <>
                <img src="/icons/lock.png" alt="" style={{ width: 16, height: 16, marginRight: 6 }} />
                Your cover photo is private
              </>
            ) : null}
          </div>
          <div className="save_changes_right">
            <button
              className="blue_btn opacity_btn"
              onClick={() => setCoverPicture("")}
            >
              Cancel
            </button>
            <button className="blue_btn " onClick={() => updateCoverPicture()}>
              {loading ? <PulseLoader color="#fff" size={5} /> : "Save changes"}
            </button>
          </div>
        </div>
      )}
      <input
        type="file"
        ref={refInput}
        hidden
        accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
        onChange={handleImage}
      />
      {error && (
        <div className="postError comment_error cover_error">
          <div className="postError_error">{error}</div>
          <button className="blue_btn" onClick={() => setError("")}>
            Try again
          </button>
        </div>
      )}
      {coverPicture && (
        <div className="cover_crooper">
          <Cropper
            image={coverPicture}
            crop={crop}
            zoom={zoom}
            aspect={width / 350}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            showGrid={true}
            objectFit="horizontal-cover"
          />
        </div>
      )}
      {(!coverPicture && (currentCover || (cover && !coverRemoved))) && (
        <img src={currentCover || cover} className="cover" alt="" ref={cRef} />
      )}
      {!visitor && (
        <div className="udpate_cover_wrapper">
          {(currentCover || (cover && !coverRemoved)) ? (
            <div className="cover_dropdown_container">
              <div
                ref={coverDropdownBtnRef}
                className="open_cover_update"
                onClick={() => setShowDropdown(!showDropdown)}
                data-tooltip="Edit Cover Photo"
                aria-label="Edit Cover Photo"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowDropdown(!showDropdown);
                  }
                }}
              >
                <i className="camera_filled_icon" aria-hidden="true"></i>
                <span className="open_cover_update_label">Edit Cover Photo</span>
              </div>
              {showDropdown && (
                <div className="cover_dropdown_menu" ref={coverDropdownMenuRef}>
                  <div className="dropdown_item" onClick={handleUploadPhoto}>
                    <i className="upload_icon"></i>
                    Upload Photo
                  </div>
                  <div className="dropdown_item" onClick={handleReposition}>
                    <i className="reposition_icon">🔄</i>
                    Reposition
                  </div>
                  <div className="dropdown_item" onClick={handleRemovePhoto}>
                    <i className="remove_icon">🗑️</i>
                    Remove Photo
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              className="open_cover_update"
              onClick={() => refInput.current.click()}
              data-tooltip="Add Cover Photo"
              aria-label="Add Cover Photo"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  refInput.current.click();
                }
              }}
            >
              <i className="camera_filled_icon" aria-hidden="true"></i>
              <span className="open_cover_update_label">Add Cover Photo</span>
            </div>
          )}
          <input
            type="file"
            ref={refInput}
            hidden
            accept="image/*"
            onChange={handleImage}
          />
        </div>
      )}
      {show && (
        <OldCovers
          photos={photos}
          setCoverPicture={setCoverPicture}
          setShow={setShow}
        />
      )}
      {showPostDialog && (
        <div className="cover_post_dialog_overlay">
          <div className="cover_post_dialog">
            <div className="cover_post_dialog_header">
              <h3>Create a Publication with your new cover picture?</h3>
            </div>
            <div className="cover_post_dialog_content">
              <div className="cover_post_dialog_image">
                <img src={currentCover || cRef.current?.src} alt="New cover" />
              </div>
              <p>Do you want to share your new cover picture as a publication so your friends can see it?</p>
            </div>
            <div className="cover_post_dialog_buttons">
              <button
                className="cancel_btn"
                onClick={() => handlePostDialogResponse(false)}
              >
                Cancel
              </button>
              <button
                className="skip_btn"
                onClick={() => handlePostDialogResponse(false)}
              >
                Skip Publication
              </button>
              <button
                className="create_post_btn blue_btn"
                onClick={() => handlePostDialogResponse(true)}
              >
                Yes, Create publication
              </button>
            </div>
          </div>
        </div>
      )}
      {showRemoveConfirmation && (
        <div className="cover_confirmation_overlay">
          <div className="cover_confirmation_dialog">
            <div className="cover_confirmation_header">
              <h3>Remove Cover Photo</h3>
            </div>
            <div className="cover_confirmation_content">
              <p>Are you sure you want to remove your cover photo? </p>
            </div>
            <div className="cover_confirmation_buttons">
              <button
                className="cancel_btn"
                onClick={cancelRemovePhoto}
              >
                Cancel
              </button>
              <button
                className="remove_btn"
                onClick={confirmRemovePhoto}
                disabled={loading}
              >
                {loading ? <PulseLoader color="#fff" size={5} /> : "Remove Photo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
